import * as React from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Moment from "moment";
import { SpirometryGraphs, FvcGraphDataMapping } from "../SpirometryGraphs";
import { WebPortalClient, FvcAnalysisSet, FvcSession, FvcAnalysisSetMutation, AnalysisSetFixedComment, FvcAnalysisSetTest, SessionNote } from "webportalclient";
import { FvcDataPoint, HomeSpirometryStatus, OverreadStatusEnum, OverreadStatusIdEnum, StudyFvcSetting } from "../../../misc/api/apiTypes";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { GraphTestData } from "../SpirometryAnalysisScreen";
import { ActiveSpirometryResultsTable } from "./ActiveSpirometryResultsTable";
import { SummaryPanel } from "../../Shared/SummaryPanel";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { UpdateBestTestNextGenAction, useAcceptabilityNextGen, AcceptabilityNextGenState, UpdateGradingAction, UpdateAnalysisSetAction, UpdateReversibilityChangeNextGenAction } from "../../Shared/UseAcceptabilityNextGen";
import { DoneLoadingBestValuesAction, DoneLoadingReversibilityChangesAction, LoadingBestValuesAction, LoadingReversibilityChangesAction } from "../../Shared/UseAcceptability";
import { ParameterTypeName } from "../../../misc/api/ParameterTypes";
import { LeanSessionMetadata, LeanSessionMetadataTable } from "../../Shared/SessionMetadataTable";
import { calculateSessionGrading } from "../../Shared/TestQualityGrading";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { useGraphs } from "../../Shared/UseGraphs";
import * as UseNextGenFixedComments from "./UseNextGenFixedComments";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { FixedCommentSetTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetTypes";
import { LocalizedDateTime, DateTimeAccuracy } from "../../../misc/components/LocalizedDateTime";
import { useInterval } from "../../../misc/Hooks/UseInterval";
import { useWebPortalApiClient, useWebPortalLegacyClient, WebPortalLegacyClientInternal } from "../../../misc/api/WebPortalTokenClient";
import { LoadSessionFixedCommentsAction, LoadTestFixedCommentsAction, useFixedCommentsDispatch, useFixedCommentsState } from "../../../Core/FixedCommentsContext";
import { FixedCommentEntityTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetEntityTypes";
import { calculateFvcRepeatabilityTopTwoNew } from "../../Shared/RepeatabilityCalculations";
import { SpirometryAnalysisSetTypeEnum } from "../../../misc/api/ApiSpirometryAnalysisSetType";
import { GoBackToList } from "../../Shared/AnalysisHelpers";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import ReactMarkdown = require("react-markdown");
import * as remarkGfm from 'remark-gfm';
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import '../../../misc/components/Formatters/MarkdownStyles.css';

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: "view" | "edit";
}

export const calculateBestValuesGrading = async (legacyClient: WebPortalLegacyClientInternal, acceptabilityState: AcceptabilityNextGenState, acceptabilityDispatch: React.Dispatch<any>, fvcSession: FvcSession, fvcSettings: StudyFvcSetting) => {
    acceptabilityDispatch(LoadingBestValuesAction.create());

    const bestTests = await legacyClient.calculateFvcBestTestsForAnalysisSet(acceptabilityState.analysisSet, fvcSession);

    acceptabilityDispatch(UpdateBestTestNextGenAction.create(
        bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_FEV1).Value,
        bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_FVC).Value,
        bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_PEFMIN).Value,
        bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_FEF2575).Value,
        bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_FEV1FVC).Value,
        bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_FEV6).Value,
        bestTests.find(bt => bt.ParameterType === ParameterTypeName.PARAM_PEFSEC).Value,
    ));

    if (fvcSettings.GradingEnabled && fvcSession.tests.length > 0) {
        const fev1Grade = calculateSessionGrading(acceptabilityState.analysisSet.tests, fvcSession.age, rs => rs.fev1Absolute, rs => rs.fev1Percentage);
        const fvcGrade = calculateSessionGrading(acceptabilityState.analysisSet.tests, fvcSession.age, rs => rs.fvcAbsolute, rs => rs.fvcPercentage);

        acceptabilityDispatch(UpdateGradingAction.create(fev1Grade, fvcGrade));
    }

    acceptabilityDispatch(DoneLoadingBestValuesAction.create());
}

export const calculateReversibilityChange = async (legacyClient: WebPortalLegacyClientInternal, acceptabilityState: AcceptabilityNextGenState, acceptabilityDispatch: React.Dispatch<any>, fvcSession: FvcSession) => {
    acceptabilityDispatch(LoadingReversibilityChangesAction.create());

    if ((acceptabilityState.analysisSet.type === SpirometryAnalysisSetTypeEnum.Site && fvcSession.status === HomeSpirometryStatus.Completed) || acceptabilityState.analysisSet.type === SpirometryAnalysisSetTypeEnum.Overread) {
        acceptabilityDispatch(DoneLoadingReversibilityChangesAction.create());
        return;
    }

    const reversibilityChangeParameters = await legacyClient.calculateReversibilityChange(acceptabilityState.analysisSet, fvcSession);
    const fev1 = reversibilityChangeParameters.find(bt => bt.ParameterType === ParameterTypeName.PARAM_FEV1);
    acceptabilityDispatch(UpdateReversibilityChangeNextGenAction.create(
        {
            absolute: fev1.Value,
            percentage: fev1.PercentageOfChange
        }
    ));
    acceptabilityDispatch(DoneLoadingReversibilityChangesAction.create());
}

export const ActiveSpirometrySessionView = () => {
    const { analysisSetId, viewState } = useParams<ExpectedMatchParams>();
    const history = useHistory();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const { fvcSettings } = useCurrentStudyConfigurationContext();
    const [analysisSet, setAnalysisSet] = React.useState<FvcAnalysisSet>();
    const [fvcSession, setFvcSession] = React.useState<FvcSession>();
    const [graphData, setGraphData] = React.useState<GraphTestData[]>();
    const client = useWebPortalApiClient();
    const [sessionMetadata, setSessionMetadata] = React.useState<LeanSessionMetadata>()
    const [error, setError] = React.useState<string | undefined>();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const legacyClient = useWebPortalLegacyClient();
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [noteError, setNoteError] = React.useState<string | undefined>();
    const [graphState, graphDispatch] = useGraphs(graphData);
    const [acceptabilityState, acceptabilityDispatch] = useAcceptabilityNextGen(analysisSet);
    const [fixedCommentsState, fixedCommentsDispatch] = UseNextGenFixedComments.useNextGenFixedComments(SessionTypes.Fvc, FixedCommentSetTypeEnum.Technician, analysisSet);

    const [updatedAnalysisSet, setUpdatedAnalysisSet] = React.useState<FvcAnalysisSet>();
    const [preAnalysisSet, setPreAnalysisSet] = React.useState<FvcAnalysisSet>();

    const [instructionsCollapsed, setInstructionsCollapsed] = React.useState<boolean>(false);
    const { study } = useCurrentStudyConfigurationContext();
    const globalFixedCommentsState = useFixedCommentsState();
    const globalFixedCommentsDispatch = useFixedCommentsDispatch();
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>([]); 

    useInterval(async () => {
        if (!fvcSession || isFetching)
            return;

        setIsFetching(true);

        const analysisSetTests = await client.client.getSpirometryAnalysisSetTests(undefined, `analysisSetId eq ${analysisSetId}`, undefined, undefined, undefined, 0, undefined, true);
        if (acceptabilityState.analysisSet.tests.length === analysisSetTests["@odata.count"]) {
            setIsFetching(false);
            return;
        }

        const siteAnalysisSetResult = await fetchSiteAnalysisSet();
        setUpdatedAnalysisSet(siteAnalysisSetResult);

        const session = await fetchSession(fvcSession.id);
        setFvcSession(session);

        const graphDataMapped = FvcGraphDataMapping(session);
        setGraphData(graphDataMapped);

        acceptabilityDispatch(UpdateAnalysisSetAction.create(siteAnalysisSetResult));

        await updateFixedComments(siteAnalysisSetResult);

        setIsFetching(false);
    }, 10000);

    const updateFixedComments = async (updatedSiteAnalysisSet: FvcAnalysisSet) => {
        const commentSets = await client.client.getStudyFixedCommentSets(undefined, `studyId eq ${study.id}`, undefined, `fixedCommentSets($expand=comments)`);

        const testSets = commentSets.value.map(ss => ({
            ...ss,
            fixedCommentSets: ss.fixedCommentSets.filter(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Test)
        }));

        for (const set of testSets) {
            globalFixedCommentsDispatch(LoadTestFixedCommentsAction.create(set.sessionType as SessionTypes, set.commentSetType as FixedCommentSetTypeEnum, null, set.fixedCommentSets.map(x => x.comments).flat()));
        }

        const testFixedComments = globalFixedCommentsState.test.get(`${SessionTypes.Fvc}-${FixedCommentSetTypeEnum.Technician}`);

        fixedCommentsDispatch(UseNextGenFixedComments.UpdateFixedCommentsAction.create(updatedSiteAnalysisSet, testFixedComments));
    }

    const fetchSession = async (sessionId: string) => {
        const fvcSessionResult = await client.client.getFvcSession(sessionId, undefined, "notes,tests,visit($select=name;$expand=subject($select=id,number;$expand=site($select=id,number,name,country)))");

        setSessionMetadata({
            Id: fvcSessionResult.id,
            SiteId: fvcSessionResult.visit.subject.site.id,
            SiteName: fvcSessionResult.visit.subject.site.name,
            SiteCountryName: fvcSessionResult.visit.subject.site.country,
            SiteNumber: fvcSessionResult.visit.subject.site.number,
            Technician: fvcSessionResult.technician,
            SubjectId: fvcSessionResult.visit.subject.id,
            SubjectNumber: fvcSessionResult.visit.subject.number,
            VisitName: fvcSessionResult.visit.name,
            SessionName: fvcSessionResult.name,
            SessionDateTime: fvcSessionResult.occurred,
            SessionTypeId: fvcSessionResult.sessionType,
            ReceivedDateTime: fvcSessionResult.created,
            SessionTypeName: fvcSessionResult.sessionType,
            Status: "",
            StatusId: "",
            Overreader: "",
            AnalysisSets: [],
        });

        setSessionNotes(fvcSessionResult.notes);
        return fvcSessionResult;
    }

    const fetchSiteAnalysisSet = async () => {
        const siteAnalysisSetResult = await client.client.getSpirometryAnalysisSet(analysisSetId, undefined, "tests($expand=test)");
        return siteAnalysisSetResult;
    }

    const getData = async () => {
        const startAnalysis = async (fvcSessionId: string, analysisSet: FvcAnalysisSet) => {
            if (analysisSet.status === OverreadStatusIdEnum.NotStarted) {
                await client.client.patchFvcSession({
                    technician: currentUser.Username,
                }, fvcSessionId);

                await client.client.patchSpirometryAnalysisSet({
                    user: currentUser.Username,
                    status: OverreadStatusIdEnum.Pending,
                }, analysisSetId);
            }
        }

        const siteAnalysisSetResult = await fetchSiteAnalysisSet();

        const fvcSessionResult = await fetchSession(siteAnalysisSetResult.sessionId);

        setAnalysisSet(siteAnalysisSetResult);
        setFvcSession(fvcSessionResult);

        if (fvcSessionResult.preSessionId !== null && fvcSessionResult.preSessionId !== undefined) {
            const preAnalysisSetResult = await client.client.getSpirometryAnalysisSets(undefined, `sessionId eq ${fvcSessionResult.preSessionId} and type eq '${SpirometryAnalysisSetTypeEnum.Site}'`);
            setPreAnalysisSet(preAnalysisSetResult.value.length > 0 ? preAnalysisSetResult.value[0] : undefined);
        }

        await startAnalysis(fvcSessionResult.id, siteAnalysisSetResult);

        fixedCommentsDispatch(UseNextGenFixedComments.LoadAnalysisSetSelectionsAction.create(siteAnalysisSetResult.fixedComments, siteAnalysisSetResult.tests));

        const graphDataMapped = FvcGraphDataMapping(fvcSessionResult);
        setGraphData(graphDataMapped);
    }

    const hasFetchedBestValues = React.useRef(false);

    React.useEffect(() => {
        const fetchBestValues = async () => {
            if (!hasFetchedBestValues.current && acceptabilityState.analysisSet !== undefined && fvcSession !== undefined) {
                hasFetchedBestValues.current = true;
                await calculateBestValuesGrading(legacyClient, acceptabilityState, acceptabilityDispatch, fvcSession, fvcSettings);
            }
        };
        fetchBestValues();
    }, [acceptabilityState.analysisSet, fvcSession]);

    React.useEffect(() => {
        getData();
    }, []);

    React.useEffect(() => {
        const fetchBestValues = async () => {
            if (acceptabilityState.analysisSet !== undefined && fvcSession !== undefined) {
                await calculateBestValuesGrading(legacyClient, acceptabilityState, acceptabilityDispatch, fvcSession, fvcSettings);
            }
        }

        const fetchReversibilityParameters = async () => {
            if (acceptabilityState.analysisSet !== undefined && fvcSession !== undefined) {
                await calculateReversibilityChange(legacyClient, acceptabilityState, acceptabilityDispatch, fvcSession);
            }
        }

        fetchBestValues();
        fetchReversibilityParameters();
    }, [acceptabilityState.changedAcceptability]);

    const addNewNote = async (annotation: string) => {
        try {
            await client.client.postSessionNote({
                sessionId: fvcSession.id,
                userEmailAddress: currentUser.Email,
                comment: annotation,
                recorded: new Date().toISOString()
            });
        } catch (ex) {
            setNoteError(ex.message);
            return;
        }
        await fetchSession(fvcSession.id);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSession(fvcSession.id);
    }

    const setAnalysisDecision = async () => {
        setIsSubmitting(true);
        setError(undefined);

        let statusValue;

        if (acceptabilityState.analysisSet.tests.filter(t => t.accepted === true).length >= fvcSettings.AcceptableTests) {

            const fvcRepeatabilityResult = await calculateFvcRepeatabilityTopTwoNew(acceptabilityState.analysisSet.tests);

            const fvcRepeatabilityThreshold = fvcSettings.AtsDeviationsFvcVolume;
            const fev1RepeatabilityThreshold = fvcSettings.AtsDeviationsFev1Volume;

            if (fvcRepeatabilityResult.fvcAbsolute <= fvcRepeatabilityThreshold && fvcRepeatabilityResult.fev1Absolute <= fev1RepeatabilityThreshold) {
                statusValue = 'ORS_ACCEPTED';
            } else {
                statusValue = 'ORS_REJECTED';
            }
        }
        else {
            statusValue = 'ORS_REJECTED';
        }

        try {
            for (const test of acceptabilityState.analysisSet.tests) {
                await client.client.patchSpirometryAnalysisSetTest({
                    accepted: test.accepted,
                    fixedComments: fixedCommentsState.testFixedComments.find(tfc => tfc.testId === test.testId).comments.filter(c => c.selected).map(c => ({
                        analysisSetTestId: test.id,
                        fixedCommentId: c.id,
                        followOnComment: c.followOnCommentAnnotation,
                    })),
                }, test.id);
            }

            await client.client.patchFvcSession({
                status: HomeSpirometryStatus.Completed,
            }, acceptabilityState.analysisSet.sessionId);

            const response = await client.client.patchSpirometryAnalysisSet({
                ...acceptabilityState.analysisSet,
                tests: undefined,
                fixedComments: fixedCommentsState.sessionFixedComments.comments.filter(c => c.selected).map(c => ({
                    analysisSetId: acceptabilityState.analysisSet.id,
                    fixedCommentId: c.id,
                    followOnComment: c.followOnCommentAnnotation,
                } as AnalysisSetFixedComment)),
                status: statusValue
            } as FvcAnalysisSetMutation, acceptabilityState.analysisSet.id);

            if (response.id !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
                setIsSubmitting(false);
            }

            GoBackToList(history, currentUserPermissions, "/sessions", PermissionEnum.AccessSingleFvcSession, "/spirometry");
        }
        catch (ex) {
            setIsSubmitting(false);
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const collapseInstructions = () => {
        setInstructionsCollapsed(!instructionsCollapsed);
    }

    if (graphState.graphData === undefined || acceptabilityState.analysisSet === undefined)
        return <LoadingWithMessage message="Loading FVC Session..." />

    const acceptanceWindowClosureTime = Moment(analysisSet.firstCompleted).add(fvcSettings.TestAcceptanceChangeTimeWindow, 'hours');
    const cannotChangeAcceptance = fvcSession.status === HomeSpirometryStatus.Completed && fvcSettings.TestAcceptanceChange && Moment().isAfter(acceptanceWindowClosureTime);

    const analysisDisabled = (fixedCommentsState.areAvailableFixedCommentsLoaded === false || (fixedCommentsState.areFixedCommentsLoaded === undefined || fixedCommentsState.areFixedCommentsLoaded === null || fixedCommentsState.areFixedCommentsLoaded === false));

    const hasAddNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.AddFvcSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewFvcSessionNotes);

    return (
        <React.StrictMode>
            <Alert show={analysisDisabled} category={AlertCategoryEnum.Danger}>
                Analysis disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>
            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            <Alert show={cannotChangeAcceptance} category={AlertCategoryEnum.Danger}>
                The session acceptability can no longer be changed, window for changes closed on <LocalizedDateTime time={acceptanceWindowClosureTime.toISOString()} accuracy={DateTimeAccuracy.Minute} />
            </Alert>

            <h2 className="text-primary">Spirometry Data</h2>
            <LeanSessionMetadataTable
                sessionMetadata={sessionMetadata}
                selectedAnalysisSetId={acceptabilityState.analysisSet.id}
            />

            {fvcSettings.HomeSpirometryInstructions ? <ErrorBoundary><h2 className="text-primary">Instructions</h2>
                <div className="panel panel-info">
                    <div className="panel-body">
                        <span className="panel-title pull-right">
                            <i className={`text-primary fas fa-${instructionsCollapsed ? 'expand' : 'compress'}-alt`} style={{ cursor: "pointer" }} onClick={collapseInstructions} />
                        </span>
                        {!instructionsCollapsed ? <ReactMarkdown remarkPlugins={[remarkGfm]} className="reactMarkdown">
                            {fvcSettings.HomeSpirometryInstructions}
                        </ReactMarkdown> : null}
                    </div>
                </div></ErrorBoundary> : null}

            <h2 className="text-primary">Charts</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="btn-group pull-right">
                            </div>
                        </div>
                    </div>
                    <SpirometryGraphs data={graphState.graphData} tZeroAdjust={false} expiratoryOnlyVisible={false} />
                </div>
            </div>

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <ActiveSpirometryResultsTable
                        acceptabilityChangesAllowed={viewState === "edit" && !cannotChangeAcceptance}
                        graphData={graphState.graphData}
                        analysisSet={acceptabilityState.analysisSet}
                        preAnalysisSet={preAnalysisSet}
                        session={fvcSession}
                        acceptabilityDispatch={acceptabilityDispatch}
                        acceptabilityState={acceptabilityState}
                        fixedCommentsState={fixedCommentsState}
                        fixedCommentsDispatch={fixedCommentsDispatch}
                        graphDispatch={graphDispatch}
                    />
                </div>
            </div>
            {hasViewNotePermission ? <SummaryPanel
                notesButtonName="Session Notes"
                showNotesCount={true}
                isLoading={false}
                notesErrorStatus={noteError}
                isAddingNotesEnabled={viewState === "edit" && hasAddNotePermission}
                notes={sessionNotes}
                onAddNote={(annotation) => addNewNote(annotation)}
                onEditNote={(id,comment, annotation) => editNote(id,comment, annotation)}
            /> : null}
            <div className="panel panel-default" hidden={!(viewState === "edit")}>
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <button className="btn btn-primary" disabled={isSubmitting} onClick={setAnalysisDecision} data-automationid="CompleteButton">
                                Complete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </React.StrictMode>
    );
}
